/**
 * Ableton GLOBAL LAUNCH quantization - label vocabulary.
 *
 * ⚠️ READ THIS BEFORE TOUCHING EITHER QUANT MODULE ⚠️
 *
 * This is NOT the record-quantization vocabulary. See
 * `abletonRecQuant.ts` for that one, and read its header - the full
 * rationale for the split lives there and is not duplicated here.
 *
 * The short version: 14 values here vs 9 there, sharing most of their label
 * text. Live's enum index `8` is **1/4T** in this vocabulary and **1/32** in
 * the record one. Getting them crossed produces a wrong result silently,
 * never an error. The two vocabularies are kept in separate modules with
 * separate parse functions so that using the wrong one requires an explicit
 * cross-module import rather than an autocomplete slip. Do NOT merge them,
 * and do NOT add a helper that serves both.
 *
 * **We never send enum indices** - labels only, per OPBridge's DEC-545. The
 * device's `set_launch_quant_enum` arm exists; we do not touch it.
 */

/** The fourteen launch-quantization labels the OP-AbletonSync device
 *  accepts, in Live's own enum order. The index IS Live's enum value, but
 *  that is documentation only - we transmit the label, never the index.
 *
 *  Note `'2'` here means a HALF NOTE (1/2), not two bars - the bar-length
 *  values carry an explicit `bars` suffix. That reads oddly at a glance and
 *  is exactly why the display map below exists. */
export const LAUNCH_QUANT_LABELS = [
  '0', '8bars', '4bars', '2bars', '1bar',
  '2', '2T', '4', '4T', '8', '8T', '16', '16T', '32',
] as const;

export type LaunchQuantLabel = (typeof LAUNCH_QUANT_LABELS)[number];

/** Human text for the picker. `'0'` is a REAL value meaning "launch
 *  immediately, no quantization", NOT the absence of a setting. */
export const LAUNCH_QUANT_DISPLAY: Record<LaunchQuantLabel, string> = {
  '0': 'None',
  '8bars': '8 Bars',
  '4bars': '4 Bars',
  '2bars': '2 Bars',
  '1bar': '1 Bar',
  '2': '1/2',
  '2T': '1/2T',
  '4': '1/4',
  '4T': '1/4T',
  '8': '1/8',
  '8T': '1/8T',
  '16': '1/16',
  '16T': '1/16T',
  '32': '1/32',
};

/** Case-insensitive lookup back to the canonical label. Built from the
 *  vocabulary itself so it cannot drift from it.
 *
 *  A plain `.toUpperCase()` cannot be used here the way it can for record
 *  quantization: `'8bars'` uppercases to `'8BARS'`, which is not a member.
 *  The mixed casing within one vocabulary (`8bars` lowercase, `8T`
 *  uppercase) is Live's, not ours. */
const CANONICAL_BY_LOWER: ReadonlyMap<string, LaunchQuantLabel> = new Map(
  LAUNCH_QUANT_LABELS.map((l) => [l.toLowerCase(), l] as const),
);

/**
 * Validate + canonicalise a launch-quantization label.
 *
 * Returns the canonical label, or `null` when the input is not a launch
 * quantization value. Callers MUST check for `null` and skip the send - an
 * unrecognised label is a **silent no-op device-side**, so this check is the
 * only thing between a typo and a control that quietly does nothing.
 *
 * ⚠️ `'0'` is a REAL label meaning None and normalises successfully.
 * Empty / whitespace / undefined means "leave Ableton's setting alone" and
 * returns `null` so nothing is sent. Never collapse those two cases -
 * sending `'0'` for "unset" would actively switch the operator's launch
 * quantization off, which on stage means every clip fires the instant it is
 * pressed instead of on the bar.
 */
export function normalizeLaunchQuant(raw: string | null | undefined): LaunchQuantLabel | null {
  if (raw == null) return null;
  const trimmed = raw.trim().replace(/"/g, '');
  if (trimmed === '') return null;
  return CANONICAL_BY_LOWER.get(trimmed.toLowerCase()) ?? null;
}

/**
 * Render a launch-quantization label for display.
 *
 * `null` / `undefined` means "we have not heard from the device" and renders
 * as `'--'`, NEVER as `'None'`. `'None'` is Live telling us quantization is
 * off; `'--'` is us not knowing. Rendering absent as None is a wrong answer
 * wearing the costume of a real one.
 */
export function displayLaunchQuant(label: string | null | undefined): string {
  const norm = normalizeLaunchQuant(label);
  return norm === null ? '--' : LAUNCH_QUANT_DISPLAY[norm];
}
